import { PRODUCTION_LANGUAGE_CODES, normalizeLanguageCode } from '../i18n/languages.js';
import {
  MARKETING_VERSION_V1,
  MARKETING_VERSION_V2,
  MARKETING_VERSION_V3,
  resolveMarketingVersion,
} from '../config/marketingVersion.js';

export const SEO_DEFAULT_LOCALE = 'en';
export const SEO_SUPPORTED_LOCALES = Object.freeze([...PRODUCTION_LANGUAGE_CODES]);
export const SEO_SITE_NAME = 'cedrum';
export const SEO_SITE_ORIGIN_FALLBACK = 'https://cedrum.app';
export const SEO_CONTACT_EMAIL = 'contact@cedrum.app';

export const SEO_COMING_SOON_MODE = 'coming-soon';
export const SEO_FULL_MODE = 'full';
export const SEO_MARKETING_VERSION_V1 = MARKETING_VERSION_V1;
export const SEO_MARKETING_VERSION_V2 = MARKETING_VERSION_V2;
export const SEO_MARKETING_VERSION_V3 = MARKETING_VERSION_V3;

export const SEO_PUBLIC_PATHS_FULL = Object.freeze([
  '/',
  '/impressum',
  '/privacy',
  '/terms',
  '/dpa',
  '/subprocessors',
  '/security',
  '/refunds-and-cancellation',
  '/withdrawal',
]);

export const SEO_PUBLIC_PATHS_COMING_SOON = Object.freeze([
  '/',
  '/impressum',
  '/privacy',
]);

const HOMEPAGE_META_BY_VARIANT = Object.freeze({
  'coming-soon': Object.freeze({
    en: Object.freeze({
      title: 'cedrum Coming Soon | Premium Gantt Charts and Boardroom-Ready Timelines',
      description:
        'cedrum is building premium gantt charts, boardroom-ready timelines, executive-ready schedules, and polished project timelines for consultants, PMOs, agencies, operators, founders, and client-facing teams.',
    }),
    de: Object.freeze({
      title: 'cedrum demnächst | Designorientierte Gantt-Charts fuer präzise Kommunikation',
      description:
        'cedrum baut einen Timeline-Editor fuer designorientierte Gantt-Charts und praesentationsreife Zeitplaene fuer Beratungen, PMOs, Agenturen, Operations-Teams, Gruender:innen und Delivery-Teams.',
    }),
    es: Object.freeze({
      title: 'cedrum Proximamente | Gantt con diseno para equipos exigentes',
      description:
        'cedrum esta creando un editor de lineas de tiempo con Gantt de diseno y planes listos para presentar para consultoria, PMO, agencias, operaciones, fundadores y equipos de delivery.',
    }),
    fr: Object.freeze({
      title: 'cedrum Bientot | Gantt design et timelines prêtes a presenter',
      description:
        'cedrum construit un editeur de timelines alliant structure de planning et controle visuel, avec des exports prets pour clients et direction pour les equipes conseil, PMO, agences, operations et delivery.',
    }),
  }),
  'full-v1': Object.freeze({
    en: Object.freeze({
      title: 'cedrum | Premium timeline and Gantt editor for presentation-ready plans',
      description:
        'cedrum is a premium timeline and Gantt editor for consultants, PMOs, agencies, and client-facing teams. Create project timelines and schedules, then export clear deliverables to PDF and PowerPoint.',
    }),
    de: Object.freeze({
      title: 'cedrum | Premium-Timeline- und Gantt-Editor für präsentationsreife Planung',
      description:
        'cedrum ist ein Premium-Timeline- und Gantt-Editor für Beratungen, PMOs, Agenturen und Teams mit Kundenkontakt. Erstelle klare Projektzeitpläne und exportiere sie als PDF oder PowerPoint.',
    }),
    es: Object.freeze({
      title: 'cedrum | Editor premium de cronogramas y Gantt para presentar',
      description:
        'cedrum es un editor premium de cronogramas y Gantt para consultoría, PMO, agencias y equipos de cara al cliente. Crea cronogramas claros y expórtalos a PDF y PowerPoint.',
    }),
    fr: Object.freeze({
      title: 'cedrum | Éditeur premium de timelines et Gantt prêts à présenter',
      description:
        'cedrum est un éditeur premium de timelines et diagrammes de Gantt pour le conseil, les PMO, les agences et les équipes en contact client. Créez des plannings clairs et exportez-les en PDF et en PowerPoint.',
    }),
  }),
  'full-v2': Object.freeze({
    en: Object.freeze({
      title: 'cedrum | Premium Gantt Charts & Project Timeline Software',
      description:
        'cedrum is a premium Gantt and timeline editor for consultants, PMOs, agencies, and client-facing teams. Create polished project timelines and export them to PDF and PowerPoint.',
    }),
    de: Object.freeze({
      title: 'cedrum | Premium-Gantt-Charts und Projektzeitpläne',
      description:
        'cedrum ist ein Premium-Editor für Gantt-Charts und Projektzeitpläne für Beratungen, PMOs, Agenturen und Teams mit Kundenkontakt. Erstelle saubere Timelines und exportiere sie als PDF oder PowerPoint.',
    }),
    es: Object.freeze({
      title: 'cedrum | Diagramas de Gantt y cronogramas premium',
      description:
        'cedrum es un editor premium de diagramas de Gantt y cronogramas para consultoría, PMO, agencias y equipos de cara al cliente. Crea planes pulidos y expórtalos a PDF y PowerPoint.',
    }),
    fr: Object.freeze({
      title: 'cedrum | Diagrammes de Gantt et timelines premium',
      description:
        'cedrum est un éditeur premium de diagrammes de Gantt et de timelines pour les cabinets de conseil, PMO, agences et équipes en contact client. Créez des plannings soignés et exportez-les en PDF et en PowerPoint.',
    }),
  }),
  'full-v3': Object.freeze({
    en: Object.freeze({
      title: 'cedrum | Premium Gantt Charts & Client-Ready Project Timelines',
      description:
        'cedrum is a premium Gantt chart and timeline editor for consultants, PMOs, agencies, and client-facing teams. Create polished project timelines and export them to PDF and PowerPoint.',
    }),
    de: Object.freeze({
      title: 'cedrum | Premium-Gantt-Charts und kundenreife Projektzeitpläne',
      description:
        'cedrum ist ein Premium-Editor für Gantt-Charts und Projektzeitpläne für Beratungen, PMOs, Agenturen und Teams mit Kundenkontakt. Erstelle saubere Timelines und exportiere sie als PDF oder PowerPoint.',
    }),
    es: Object.freeze({
      title: 'cedrum | Diagramas de Gantt premium y cronogramas listos para clientes',
      description:
        'cedrum es un editor premium de diagramas de Gantt y cronogramas para consultoría, PMO, agencias y equipos de cara al cliente. Crea planes pulidos y expórtalos a PDF y PowerPoint.',
    }),
    fr: Object.freeze({
      title: 'cedrum | Diagrammes de Gantt premium et timelines prêtes pour les clients',
      description:
        'cedrum est un éditeur premium de diagrammes de Gantt et de timelines pour les cabinets de conseil, PMO, agences et équipes en contact client. Créez des plannings soignés et exportez-les en PDF et en PowerPoint.',
    }),
  }),
});

const HOMEPAGE_SOCIAL_CARD_BY_VARIANT = Object.freeze({
  'coming-soon': Object.freeze({
    imagePath: '/og/cedrum-coming-soon-og.svg',
    imageAlt: 'cedrum coming soon preview showing a premium timeline interface',
  }),
  'full-v1': Object.freeze({
    imagePath: '/og/cedrum-full-og.svg',
    imageAlt: 'cedrum premium timeline editor preview with project schedule and export outcomes',
  }),
  'full-v2': Object.freeze({
    imagePath: '/og/cedrum-v2-og.svg',
    imageAlt: 'cedrum premium Gantt and timeline editor preview',
  }),
  'full-v3': Object.freeze({
    imagePath: '/og/cedrum-v3-og.svg',
    imageAlt: 'cedrum premium Gantt chart and timeline editor preview',
  }),
});

function normalizePathname(pathname) {
  const raw = String(pathname || '/').trim();
  if (!raw) {
    return '/';
  }

  const prefixed = raw.startsWith('/') ? raw : `/${raw}`;
  if (prefixed.length > 1 && prefixed.endsWith('/')) {
    return prefixed.slice(0, -1);
  }

  return prefixed;
}

function resolveHomepageVariant(siteMode = SEO_FULL_MODE, marketingVersion = SEO_MARKETING_VERSION_V1) {
  if (normalizeSeoSiteMode(siteMode) === SEO_COMING_SOON_MODE) {
    return 'coming-soon';
  }

  const resolvedVersion = normalizeSeoMarketingVersion(marketingVersion);
  if (resolvedVersion === SEO_MARKETING_VERSION_V2) {
    return 'full-v2';
  }

  if (resolvedVersion === SEO_MARKETING_VERSION_V3) {
    return 'full-v3';
  }

  return 'full-v1';
}

export function normalizeSeoSiteMode(siteMode = SEO_FULL_MODE) {
  const normalized = String(siteMode || '').trim().toLowerCase();
  if (normalized === SEO_COMING_SOON_MODE || normalized === SEO_FULL_MODE) {
    return normalized;
  }

  return SEO_FULL_MODE;
}

export function normalizeSeoMarketingVersion(marketingVersion = SEO_MARKETING_VERSION_V1) {
  return resolveMarketingVersion(marketingVersion);
}

export function getSeoPublicPaths(siteMode = SEO_FULL_MODE) {
  if (normalizeSeoSiteMode(siteMode) === SEO_COMING_SOON_MODE) {
    return SEO_PUBLIC_PATHS_COMING_SOON;
  }

  return SEO_PUBLIC_PATHS_FULL;
}

export function normalizeSeoLocale(locale) {
  const normalized = normalizeLanguageCode(locale);
  return SEO_SUPPORTED_LOCALES.includes(normalized) ? normalized : SEO_DEFAULT_LOCALE;
}

export function getHomepageMeta(
  locale = SEO_DEFAULT_LOCALE,
  siteMode = SEO_FULL_MODE,
  marketingVersion = SEO_MARKETING_VERSION_V1,
) {
  const normalizedLocale = normalizeSeoLocale(locale);
  const variant = resolveHomepageVariant(siteMode, marketingVersion);
  const variantMeta = HOMEPAGE_META_BY_VARIANT[variant] || HOMEPAGE_META_BY_VARIANT['full-v1'];
  return variantMeta[normalizedLocale] || variantMeta[SEO_DEFAULT_LOCALE];
}

export function getHomepageSocialCard(
  siteMode = SEO_FULL_MODE,
  marketingVersion = SEO_MARKETING_VERSION_V1,
) {
  const variant = resolveHomepageVariant(siteMode, marketingVersion);
  return HOMEPAGE_SOCIAL_CARD_BY_VARIANT[variant] || HOMEPAGE_SOCIAL_CARD_BY_VARIANT['full-v1'];
}

export function resolveSeoOrigin(origin = SEO_SITE_ORIGIN_FALLBACK) {
  const fallback = new URL(SEO_SITE_ORIGIN_FALLBACK);

  try {
    const next = new URL(String(origin || SEO_SITE_ORIGIN_FALLBACK).trim());
    return `${next.protocol}//${next.host}`;
  } catch {
    return `${fallback.protocol}//${fallback.host}`;
  }
}

export function getLocaleUrl({
  origin = SEO_SITE_ORIGIN_FALLBACK,
  pathname = '/',
  locale = SEO_DEFAULT_LOCALE,
} = {}) {
  const resolvedOrigin = resolveSeoOrigin(origin);
  const normalizedLocale = normalizeSeoLocale(locale);
  const normalizedPath = normalizePathname(pathname);
  const url = new URL(normalizedPath, resolvedOrigin);

  if (normalizedLocale === SEO_DEFAULT_LOCALE) {
    url.searchParams.delete('lang');
  } else {
    url.searchParams.set('lang', normalizedLocale);
  }

  return url.toString();
}

export function getCanonicalUrl({
  origin = SEO_SITE_ORIGIN_FALLBACK,
  pathname = '/',
  locale = SEO_DEFAULT_LOCALE,
} = {}) {
  return getLocaleUrl({ origin, pathname, locale });
}

export function getHreflangAlternates({
  origin = SEO_SITE_ORIGIN_FALLBACK,
  pathname = '/',
} = {}) {
  const alternates = SEO_SUPPORTED_LOCALES.map((locale) => ({
    hrefLang: locale,
    href: getLocaleUrl({ origin, pathname, locale }),
  }));

  alternates.push({
    hrefLang: 'x-default',
    href: getLocaleUrl({ origin, pathname, locale: SEO_DEFAULT_LOCALE }),
  });

  return alternates;
}

export function isHomepagePath(pathname) {
  return normalizePathname(pathname) === '/';
}

export function buildOrganizationSchema({ origin = SEO_SITE_ORIGIN_FALLBACK } = {}) {
  const resolvedOrigin = resolveSeoOrigin(origin);

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SEO_SITE_NAME,
    url: resolvedOrigin,
    logo: new URL('/apple-touch-icon.png', resolvedOrigin).toString(),
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: SEO_CONTACT_EMAIL,
        availableLanguage: SEO_SUPPORTED_LOCALES,
      },
    ],
  };
}

export function buildWebsiteSchema({
  origin = SEO_SITE_ORIGIN_FALLBACK,
  locale = SEO_DEFAULT_LOCALE,
  description = '',
} = {}) {
  const resolvedOrigin = resolveSeoOrigin(origin);
  const normalizedLocale = normalizeSeoLocale(locale);

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SEO_SITE_NAME,
    url: resolvedOrigin,
    inLanguage: normalizedLocale,
    description: String(description || ''),
  };
}

export function buildSoftwareApplicationSchema({
  origin = SEO_SITE_ORIGIN_FALLBACK,
  locale = SEO_DEFAULT_LOCALE,
  description = '',
} = {}) {
  const resolvedOrigin = resolveSeoOrigin(origin);
  const normalizedLocale = normalizeSeoLocale(locale);

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SEO_SITE_NAME,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    inLanguage: normalizedLocale,
    url: getLocaleUrl({ origin: resolvedOrigin, pathname: '/', locale: normalizedLocale }),
    description: String(description || ''),
  };
}

export function buildFaqSchema({ faqItems = [] } = {}) {
  if (!Array.isArray(faqItems) || faqItems.length === 0) {
    return null;
  }

  const items = faqItems
    .map((item) => {
      const question = String(item?.question || '').trim();
      const answer = String(item?.answer || '').trim();
      if (!question || !answer) {
        return null;
      }

      return {
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      };
    })
    .filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items,
  };
}
